import React, { useCallback, useEffect, useState } from 'react';
import { TYPE, useTheme } from '../theme';
import { Button, Card, ErrorState, Input, Loader } from '../components/ui';
import { authHeaders } from '../api';
import { apiFetch } from '../appFetch';

// Admin → Q: Anthropic-nyckeln och gränsen per person och dag för Q (apps/api/src/q/settings.ts).
// Nyckeln går bara att skriva: den krypteras på servern och skickas aldrig tillbaka, bara de fyra sista tecknen.
// CV2: samma sak för Qvenaris OpenAI-konto (ChatGPT-rösten i Q, apps/api/src/q/realtime.ts) med en egen gräns.

interface KeyStatus { set: boolean; source: 'admin' | 'env' | 'none'; last4: string | null; updatedAt: string | null; updatedBy: string | null }
type Usage = { day: string; userId: string; email: string | null; calls: number }[];
interface QAdminSettings { key: KeyStatus; limit: number | null; usage: Usage; openai?: { key: KeyStatus; limit: number | null; usage: Usage } }

async function call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await apiFetch(path, { method, headers: { ...authHeaders(), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) }, body: body !== undefined ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Fel ${res.status}`);
  return data as T;
}

const SOURCE: Record<KeyStatus['source'], string> = { admin: 'satt i appen', env: 'från Secret Manager', none: 'ingen nyckel – Q kör i låtsasläget' };

export function QAdminPanel() {
  const T = useTheme();
  const [s, setS] = useState<QAdminSettings | null>(null);
  const [error, setError] = useState('');
  const [key, setKey] = useState('');
  const [limit, setLimit] = useState('');
  const [oaKey, setOaKey] = useState('');
  const [oaLimit, setOaLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<{ ok: boolean; message: string } | null>(null);

  const apply = (next: QAdminSettings) => { setS(next); setLimit(next.limit == null ? '' : String(next.limit)); setOaLimit(next.openai?.limit == null ? '' : String(next.openai.limit)); };
  const load = useCallback(() => { call<QAdminSettings>('/api/q/settings').then(apply).catch(e => setError(e.message)); }, []);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(''); setTest(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  if (!s) return error ? <ErrorState message={error} /> : <Loader text="Hämtar Q:s inställningar…" />;
  const label: React.CSSProperties = { color: T.text, fontWeight: 700, marginBottom: 4 };
  const hint: React.CSSProperties = { ...TYPE.small, color: T.textSub, margin: '0 0 10px' };
  const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ ...TYPE.small, color: T.textSub, margin: 0 }}>Q är röst- och chattassistenten som öppnas med loggan. Den kör Claude med nyckeln här och kan använda Qvenaris verktyg som den inloggade jägaren.</p>
      {error && <ErrorState message={error} />}
      <Card padding={16}>
        <div style={label}>Anthropic API-nyckel</div>
        <p style={hint}>
          {s.key.set ? `Satt (…${s.key.last4 ?? '????'}), ${SOURCE[s.key.source]}` : SOURCE.none}
          {s.key.updatedBy && s.key.updatedAt ? ` · ändrad ${new Date(s.key.updatedAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })} av ${s.key.updatedBy}` : ''}
        </p>
        <form style={row} onSubmit={e => { e.preventDefault(); void run(async () => { apply(await call('/api/q/settings/key', 'PUT', { key })); setKey(''); }); }}>
          <Input type="password" autoComplete="off" placeholder="sk-ant-…" aria-label="Ny Anthropic API-nyckel" value={key} onChange={e => setKey(e.target.value)} style={{ flex: '1 1 220px' }} />
          <Button type="submit" disabled={busy || !key.trim()}>Spara</Button>
        </form>
        <div style={{ ...row, marginTop: 10 }}>
          <Button variant="ghost" disabled={busy} onClick={() => void run(async () => setTest(await call('/api/q/settings/test', 'POST')))}>Testa</Button>
          {s.key.source === 'admin' && <Button variant="ghost" disabled={busy} onClick={() => void run(async () => apply(await call('/api/q/settings/key', 'DELETE')))}>Ta bort</Button>}
          {test && <span role="status" style={{ ...TYPE.small, color: test.ok ? T.success : T.danger }}>{test.message}</span>}
        </div>
      </Card>
      <Card padding={16}>
        <div style={label}>Anrop per person och dag</div>
        <p style={hint}>Tomt = ingen gräns.</p>
        <form style={row} onSubmit={e => { e.preventDefault(); void run(async () => apply(await call('/api/q/settings/limit', 'PUT', { limit: limit.trim() === '' ? null : Number(limit) }))); }}>
          <Input inputMode="numeric" placeholder="Ingen gräns" aria-label="Anrop per person och dag" value={limit} onChange={e => setLimit(e.target.value.replace(/[^0-9]/g, ''))} style={{ width: 140 }} />
          <Button type="submit" variant="ghost" disabled={busy}>Spara gräns</Button>
        </form>
      </Card>
      {s.openai && (
        <Card padding={16}>
          <div style={label}>OpenAI API-nyckel (ChatGPT-rösten)</div>
          <p style={hint}>
            Qvenaris konto för röstalternativet ChatGPT i Q. Personer kan också lägga in en egen nyckel i Q:s inställningar.{' '}
            {s.openai.key.set ? `Satt (…${s.openai.key.last4 ?? '????'}), ${s.openai.key.source === 'admin' ? 'satt i appen' : 'från Secret Manager'}.` : 'Ingen nyckel – bara egna nycklar fungerar.'}
          </p>
          <form style={row} onSubmit={e => { e.preventDefault(); void run(async () => { apply(await call('/api/q/settings/openai-key', 'PUT', { key: oaKey })); setOaKey(''); }); }}>
            <Input type="password" autoComplete="off" placeholder="sk-…" aria-label="Ny OpenAI API-nyckel" value={oaKey} onChange={e => setOaKey(e.target.value)} style={{ flex: '1 1 220px' }} />
            <Button type="submit" disabled={busy || !oaKey.trim()}>Spara</Button>
            {s.openai.key.source === 'admin' && <Button variant="ghost" disabled={busy} onClick={() => void run(async () => apply(await call('/api/q/settings/openai-key', 'DELETE')))}>Ta bort</Button>}
          </form>
          <p style={{ ...hint, margin: '12px 0 6px' }}>Samtal per person och dag på Qvenaris konto (tomt = ingen gräns). I dag {s.openai.usage.filter(u => u.day === new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' })).reduce((n, u) => n + u.calls, 0)} samtal totalt.</p>
          <form style={row} onSubmit={e => { e.preventDefault(); void run(async () => apply(await call('/api/q/settings/openai-limit', 'PUT', { limit: oaLimit.trim() === '' ? null : Number(oaLimit) }))); }}>
            <Input inputMode="numeric" placeholder="Ingen gräns" aria-label="ChatGPT-samtal per person och dag" value={oaLimit} onChange={e => setOaLimit(e.target.value.replace(/[^0-9]/g, ''))} style={{ width: 140 }} />
            <Button type="submit" variant="ghost" disabled={busy}>Spara gräns</Button>
          </form>
        </Card>
      )}
      <Card padding={16}>
        <div style={label}>Användning, sju dagar</div>
        {s.usage.length === 0 ? <p style={{ ...hint, margin: 0 }}>Ingen har använt Q än.</p> : (
          <div style={{ display: 'grid', gap: 4 }}>
            {s.usage.map(u => (
              <div key={`${u.day}:${u.userId}`} style={{ ...TYPE.small, display: 'flex', gap: 10, color: T.text }}>
                <span style={{ color: T.textMuted, width: 86, flexShrink: 0 }}>{u.day}</span>
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{u.email ?? u.userId}</span>
                <span style={{ fontWeight: 700 }}>{u.calls}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
